import React, { useContext, useEffect, useState, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useParams } from "react-router-dom";
import { AuthContext } from "../../../../config/AuthContext";
import { 
    Button, Table, Tag, Typography, Card, Tooltip, Row, Col, 
    Statistic, Progress, Avatar, Space, Badge, Input 
} from "antd";
import { ColumnsType, TableProps } from "antd/es/table";
import { 
    TeamOutlined, UserOutlined, CheckCircleOutlined, CloseCircleOutlined,
    AreaChartOutlined, ReloadOutlined, SearchOutlined, EyeOutlined,
    TrophyOutlined, FieldTimeOutlined
} from '@ant-design/icons';
import { FaCheck, FaTimes } from "react-icons/fa";
import { GetUsuariIdJutgeActivitatsAlumnes } from "../../../../dao/GetUsuariIdJutgeActivitatsAlumnes";
import { GetUsuariIdJutgeActivitats } from "../../../../dao/jutge/GetUsuariIdJutgeActivitats";
import { GetRankingsUsuariId } from "../../../../dao/jutge/GetRankingsUsuariId";
import { GetUsuariIdJutgeActivitatsId } from "../../../../dao/GetUsuariIdJutgeActivitatsId";
import ActivitatsIdExercicis from "../../activitatsIdAlumnesId/content/ActivitatsIdExercicis";
import JutgeColors from "../../../../styles/JutgeColors";

const { Title, Text } = Typography;

const ActivitatsIdAlumnes = () => {
    const { isLoggedIn, userId, user, setBreadCrumb } = useContext(AuthContext);
    const { id_activitat } = useParams();
    const [dades, setDades] = useState<any[]>([]);
    const [avatars, setAvatars] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [loadingStats, setLoadingStats] = useState(false);
    const [searchText, setSearchText] = useState('');

    // Estado para almacenar qué alumno tiene cargadas sus dades
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
    const [subData, setSubData] = useState<Record<string, any[]>>({});

    useEffect(() => {
        if (isLoggedIn) {
            checkDades();
        }
    }, [id_activitat]);

    async function checkDades() {
        setLoading(true);
        try {
            // Carga concurrente de alumnos, metadatos de actividad y avatares de ranking
            const [dad, prac, rankData] = await Promise.all([
                GetUsuariIdJutgeActivitatsAlumnes(id_activitat + ""),
                GetUsuariIdJutgeActivitats(userId),
                GetRankingsUsuariId(userId + "")
            ]);

            const actividad = Array.isArray(prac) ? prac.find((act: any) => act.id === Number(id_activitat)) : undefined;
            if (actividad) {
                setBreadCrumb(
                    [
                        { nom: 'Home', nav: '/home' },
                        { nom: 'jutge', nav: '/jutge' },
                        { nom: 'activitats', nav: `/jutge/activitats` },
                        { nom: `${actividad.nom}`, nav: `/jutge/activitats/${id_activitat}` }
                    ],
                    true
                );
            }

            if (rankData) {
                const ranking = Array.isArray(rankData) ? rankData : rankData.ranking || [];
                const map: Record<string, string> = {};
                ranking.forEach((r: any) => {
                    const uid = r.id_usuari || r.id;
                    if (uid && r.avatar) map[String(uid)] = r.avatar;
                });
                setAvatars(map);
            }

            const initialStudents = dad || [];
            // Conservamos métricas anteriores si ya estaban para evitar parpadeo
            setDades(prev => {
                const prevMap = new Map((prev || []).map((s: any) => [s.id_usuari, s]));
                return initialStudents.map((s: any) => {
                    const old = prevMap.get(s.id_usuari);
                    if (!old) return s;
                    return {
                        ...s,
                        intents: old.intents ?? s.intents ?? 0,
                        be: old.be ?? s.be ?? 0,
                        malament: old.malament ?? s.malament ?? 0,
                        progres: old.progres ?? s.progres ?? 0
                    };
                });
            });
            setLoading(false);

            if (initialStudents.length > 0) {
                loadStatsProgressively(initialStudents);
            }
        } catch (e) {
            console.error(e);
            setLoading(false);
        }
    }

    async function loadStatsProgressively(students: any[]) {
        setLoadingStats(true);
        const batchSize = 4;
        for (let i = 0; i < students.length; i += batchSize) {
            const batch = students.slice(i, i + batchSize);
            const batchResults = await Promise.all(
                batch.map(async (student: any) => {
                    try {
                        const exercicis = await GetUsuariIdJutgeActivitatsId(student.id_usuari + "", id_activitat + "");
                        return { id_usuari: student.id_usuari, exercicis: exercicis || [] };
                    } catch {
                        return { id_usuari: student.id_usuari, exercicis: [] };
                    }
                })
            );

            setSubData(prev => {
                const updated = { ...prev };
                batchResults.forEach(r => { updated[r.id_usuari] = r.exercicis; });
                return updated;
            });

            setDades(prevList => {
                return (prevList || []).map(st => {
                    const stId = String(st.id_usuari ?? st.numericId ?? st.id);
                    const found = batchResults.find(b => String(b.id_usuari) === stId);
                    if (!found || !found.exercicis || found.exercicis.length === 0) return st;

                    const totalExercicis = found.exercicis.length;
                    const totalTests = found.exercicis.reduce((acc: number, ex: any) => acc + (Number(ex.tests) || 0), 0);
                    const totalBe = found.exercicis.reduce((acc: number, ex: any) => acc + (Number(ex.progres2) || (Number(ex.progres) === 1 ? (Number(ex.tests) || 1) : 0)), 0);
                    const totalIntents = found.exercicis.reduce((acc: number, ex: any) => acc + (Number(ex.intents) || 0), 0);

                    let progres = 0;
                    if (totalTests > 0) {
                        progres = (totalBe / totalTests) * 100;
                    } else if (totalExercicis > 0) {
                        const sumProgres = found.exercicis.reduce((acc: number, ex: any) => {
                            if (Number(ex.progres) > 0) return acc + Number(ex.progres);
                            if (ex.origen_success !== undefined && ex.origen_success !== null) return acc + 1;
                            return acc;
                        }, 0);
                        progres = (sumProgres / totalExercicis) * 100;
                    }

                    return {
                        ...st,
                        intents: totalIntents,
                        be: totalBe,
                        malament: Math.max(0, (totalIntents - totalBe)),
                        progres: Number(Math.min(100, Math.max(0, progres)).toFixed(1))
                    };
                });
            });
        }
        setLoadingStats(false);
    }

    const handleExpand = async (expanded: boolean, record: any) => {
        if (expanded) {
            if (!subData[record.id_usuari]) {
                const exercicis = await GetUsuariIdJutgeActivitatsId(record.id_usuari + "", id_activitat + "");
                setSubData(prev => ({ ...prev, [record.id_usuari]: exercicis || [] }));
            }
            setExpandedRowKeys([record.id_usuari]);
        } else {
            setExpandedRowKeys(expandedRowKeys.filter(k => k !== record.id_usuari));
        }
    };

    function getAvatarSrc(img: string | undefined | null) {
        if (!img || img === "undefined" || img === "null") return undefined;
        if (img.startsWith("http") || img.startsWith("data:") || img.startsWith("/")) return img;
        if (img.startsWith("base64:")) img = img.split(":").pop()!;
        if (img.startsWith("/9j/")) return `data:image/jpeg;base64,${img}`;
        if (img.startsWith("iVB")) return `data:image/png;base64,${img}`;
        return `data:image/png;base64,${img}`;
    }

    const stringToColor = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        let c = (hash & 0x00ffffff).toString(16).toUpperCase();
        return '#' + '00000'.substring(0, 6 - c.length) + c;
    };

    // Estadísticas globales resumen similares a Estadistiques
    const globalStats = useMemo(() => {
        if (!dades || dades.length === 0) {
            return { avgProgress: 0, totalBe: 0, totalMalament: 0, totalIntents: 0, passedCount: 0 };
        }
        let totalProg = 0;
        let totalBe = 0;
        let totalMalament = 0;
        let totalIntents = 0;
        let passedCount = 0;

        dades.forEach(s => {
            const p = Number(s.progres) || 0;
            totalProg += p;
            totalBe += (s.be || 0);
            totalMalament += (s.malament || 0);
            totalIntents += (s.intents || 0);
            if (p >= 50) passedCount++;
        });

        return {
            avgProgress: totalProg / dades.length,
            totalBe,
            totalMalament,
            totalIntents,
            passedCount
        };
    }, [dades]);

    const filteredDades = useMemo(() => {
        if (!searchText.trim()) return dades;
        const q = searchText.toLowerCase();
        return dades.filter(s => 
            (s.usuari && s.usuari.toLowerCase().includes(q)) ||
            String(s.id_usuari).includes(q)
        );
    }, [dades, searchText]);

    const columns: ColumnsType<any> = [
        {
            key: 'usuari',
            title: 'Alumne',
            dataIndex: 'usuari',
            sorter: (a, b) => (a.usuari || '').localeCompare(b.usuari || ''),
            render: (nom, record) => {
                const avatarImg = avatars[record.id_usuari];
                const avatarSrc = getAvatarSrc(avatarImg);
                const initials = (nom || 'U').substring(0, 2).toUpperCase();
                const avatarBg = stringToColor(nom || 'User');

                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {avatarSrc ? (
                            <Avatar src={avatarSrc} size={42} style={{ border: '2px solid #e8e8e8' }} />
                        ) : (
                            <Avatar size={42} style={{ backgroundColor: avatarBg, fontWeight: 'bold' }}>
                                {initials}
                            </Avatar>
                        )}
                        <div>
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#1f1f1f' }}>{nom}</div>
                            <Tag style={{ fontSize: 11, padding: '0 6px', margin: 0, borderRadius: 6, background: '#f5f5f5', border: '1px solid #d9d9d9', color: '#666' }}>
                                ID: #{record.id_usuari}
                            </Tag>
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'intents',
            title: 'Intents',
            dataIndex: 'intents',
            align: 'center',
            sorter: (a, b) => (a.intents || 0) - (b.intents || 0),
            render: (val) => (
                <Tag color="default" style={{ fontWeight: 600, borderRadius: 8, padding: '2px 8px' }}>
                    {val || 0} enviaments
                </Tag>
            )
        },
        {
            key: 'stats',
            title: 'Tests Stats',
            align: 'center',
            render: (_, record) => (
                <Space size={8}>
                    <Tooltip title="Tests Superats Correctament">
                        <Tag 
                            color="success" 
                            icon={<CheckCircleOutlined />} 
                            style={{ borderRadius: 8, padding: '2px 8px', fontWeight: 600, fontSize: 12 }}
                        >
                            {record.be || 0}
                        </Tag>
                    </Tooltip>
                    <Tooltip title="Tests amb Errors">
                        <Tag 
                            color="error" 
                            icon={<CloseCircleOutlined />} 
                            style={{ borderRadius: 8, padding: '2px 8px', fontWeight: 600, fontSize: 12 }}
                        >
                            {record.malament || 0}
                        </Tag>
                    </Tooltip>
                </Space>
            )
        },
        {
            key: 'progres',
            title: 'Progrés / Assoliment',
            dataIndex: 'progres',
            align: 'left',
            sorter: (a, b) => (a.progres || 0) - (b.progres || 0),
            render: (nota) => {
                const val = Number(nota) || 0;
                let stroke = '#f5222d';
                if (val >= 80) stroke = '#52c41a';
                else if (val >= 50) stroke = '#1890ff';
                else if (val >= 25) stroke = '#faad14';

                return (
                    <div style={{ maxWidth: 220 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span style={{ fontWeight: 700, fontSize: 13, color: stroke }}>
                                {val.toFixed(2)}%
                            </span>
                        </div>
                        <Progress 
                            percent={val} 
                            size="small" 
                            strokeColor={stroke} 
                            showInfo={false} 
                            style={{ margin: 0 }} 
                        />
                    </div>
                );
            }
        },
        {
            key: 'actions',
            title: 'Detall',
            align: 'center',
            width: 80,
            render: (_, record) => {
                const isExpanded = expandedRowKeys.includes(record.id_usuari);
                return (
                    <Tooltip title={isExpanded ? "Plegar exercicis" : "Veure exercicis de l'alumne"}>
                        <Button 
                            type={isExpanded ? "primary" : "default"}
                            shape="circle" 
                            size="small"
                            icon={<EyeOutlined />}
                            style={isExpanded ? { background: JutgeColors.colors.color4, borderColor: JutgeColors.colors.color4 } : {}}
                            onClick={() => handleExpand(!isExpanded, record)}
                        />
                    </Tooltip>
                );
            }
        }
    ];

    const tableProps: TableProps<any> = { loading };

    return (
        <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header amb títol i accions */}
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
                <div>
                    <Title level={2} style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <TeamOutlined style={{ color: '#1890ff' }} /> Seguiment d'Alumnes
                    </Title>
                    <Text type="secondary">
                        {loadingStats 
                            ? "Sincronitzant mètriques detallades en segon pla..." 
                            : "Avaluació individual i resolució d'exercicis de l'activitat."}
                    </Text>
                </div>
                <Space size={12}>
                    <Input 
                        placeholder="Cerca alumne o ID..." 
                        prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                        style={{ width: 220, borderRadius: 8 }}
                    />
                    {(user.tipus === "1" || user.tipus === "0") && (
                        <Button
                            type="primary"
                            icon={<ReloadOutlined />}
                            onClick={() => checkDades()}
                            loading={loading || loadingStats}
                            style={{ 
                                background: '#1890ff', 
                                borderColor: '#1890ff',
                                borderRadius: 8,
                                fontWeight: 500,
                                boxShadow: '0 2px 6px rgba(24, 144, 255, 0.2)'
                            }}
                        >
                            Refrescar
                        </Button>
                    )}
                </Space>
            </div>

            {/* Targetes de resum superior (Estil idèntic a Estadístiques) */}
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                        <Statistic 
                            title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Alumnes Totals</span>} 
                            value={dades.length} 
                            prefix={<UserOutlined style={{ color: '#1890ff', marginRight: 6 }} />} 
                            valueStyle={{ fontWeight: 700, fontSize: 26 }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                        <Statistic 
                            title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Progrés Mitjà</span>} 
                            value={globalStats.avgProgress} 
                            precision={2} 
                            suffix="%" 
                            prefix={<AreaChartOutlined style={{ color: globalStats.avgProgress >= 50 ? '#52c41a' : '#faad14', marginRight: 6 }} />}
                            valueStyle={{ color: globalStats.avgProgress >= 50 ? '#52c41a' : '#faad14', fontWeight: 700, fontSize: 26 }} 
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                        <Statistic 
                            title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Aprovats (≥50%)</span>} 
                            value={globalStats.passedCount} 
                            suffix={<span style={{ fontSize: 14, color: '#8c8c8c' }}>/ {dades.length}</span>}
                            prefix={<TrophyOutlined style={{ color: '#faad14', marginRight: 6 }} />}
                            valueStyle={{ color: '#52c41a', fontWeight: 700, fontSize: 26 }} 
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' }} bodyStyle={{ padding: '16px 20px' }}>
                        <Statistic 
                            title={<span style={{ color: '#8c8c8c', fontSize: 13 }}>Tests Superats</span>} 
                            value={globalStats.totalBe} 
                            prefix={<CheckCircleOutlined style={{ color: '#52c41a', marginRight: 6 }} />}
                            valueStyle={{ color: '#1890ff', fontWeight: 700, fontSize: 26 }} 
                        />
                    </Card>
                </Col>
            </Row>

            {/* Taula principal amb estil Premium */}
            <Card 
                bordered={false} 
                bodyStyle={{ padding: 0 }} 
                style={{ 
                    borderRadius: 14, 
                    overflow: 'hidden', 
                    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
                    background: '#ffffff'
                }}
            >
                <Table
                    size="middle"
                    bordered={false}
                    {...tableProps}
                    pagination={{
                        showSizeChanger: true,
                        pageSizeOptions: ['15', '30', '50', '100'],
                        defaultPageSize: 15,
                        style: { padding: '12px 24px', margin: 0 }
                    }}
                    columns={columns}
                    dataSource={filteredDades}
                    rowKey="id_usuari"
                    expandable={{
                        expandedRowRender: (record) => (
                            <div style={{ 
                                padding: "16px 28px", 
                                background: '#fafbfc', 
                                borderTop: '1px solid #f0f0f0',
                                borderBottom: '1px solid #f0f0f0' 
                            }}>
                                <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Text strong style={{ fontSize: 13, color: '#595959' }}>
                                        Exercicis de l'alumne:
                                    </Text>
                                    <Tag color="blue" style={{ borderRadius: 6 }}>{record.usuari}</Tag>
                                </div>
                                <ActivitatsIdExercicis
                                    id_activitat={String(id_activitat || '')}
                                    id_alumne={record.id_usuari}
                                    dades={subData[record.id_usuari] || []}
                                />
                            </div>
                        ),
                        expandedRowKeys,
                        onExpand: handleExpand,
                    }}
                />
            </Card>
        </div>
    );
};

export default ActivitatsIdAlumnes;
